// src/auth.js
import { apiFetch } from "./lib/api";

// ── Storage keys ─────────────────────────────────────────────────────────────
// User profile only — NOT the token (token stays in httpOnly cookie + memory)
const USER_KEY     = "session:user";
const PROP_KEY     = "selectedProperty";

// ── In-memory token store (never persisted to localStorage) ──────────────────
// Falls back to reading the access-token returned in the login response body
// for the current tab. On page refresh, the /api/auth/refresh endpoint is called
// silently to get a new access token using the httpOnly refresh-token cookie.
let _memToken = "";

export function setMemToken(t)  { _memToken = t || ""; }
export function getMemToken()   { return _memToken; }

// ────────────────────────────────────────────────────────────────────────────
// Login
// ────────────────────────────────────────────────────────────────────────────
export async function login({ customerId, password }) {
  const data = await apiFetch("/api/auth/login", {
    method: "POST",
    body:   { customerId, password },
  });

  if (data.token) {
    setMemToken(data.token);

    // Store only non-sensitive profile info in sessionStorage (cleared on tab close)
    sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));

    if (data.user?.assignedProperties?.length > 0) {
      sessionStorage.setItem(PROP_KEY, JSON.stringify(data.user.assignedProperties[0]));
    }
  }

  return data;
}

// ────────────────────────────────────────────────────────────────────────────
// Silent refresh — called on app boot or when a 401 is received
// ────────────────────────────────────────────────────────────────────────────
export async function silentRefresh() {
  try {
    const data = await apiFetch("/api/auth/refresh", {
      method:      "POST",
      credentials: "include",   // sends the httpOnly rf_token cookie
    });
    if (data?.token) {
      setMemToken(data.token);
      // Restore user profile to sessionStorage so getSession() works in new tabs
      if (data.user) {
        sessionStorage.setItem(USER_KEY, JSON.stringify(data.user));
        // Only set the selected property if this tab doesn't already have one stored
        if (!sessionStorage.getItem(PROP_KEY) && data.user.assignedProperties?.length > 0) {
          sessionStorage.setItem(PROP_KEY, JSON.stringify(data.user.assignedProperties[0]));
        }
      }
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Session accessors
// ────────────────────────────────────────────────────────────────────────────
export function getSession() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getSelectedProperty() {
  try {
    const raw = sessionStorage.getItem(PROP_KEY);
    if (!raw) return { code: "", name: "", id: "" };
    const p = JSON.parse(raw);
    return {
      code: p.code || p.propertyId || p._id || "",
      name: p.name || p.title || "Unknown Property",
      id:   p._id  || p.id   || "",
    };
  } catch {
    return { code: "", name: "", id: "" };
  }
}

/** Returns the in-memory access token. Used by the apiFetch wrapper. */
export function getToken() {
  return _memToken;
}

// ────────────────────────────────────────────────────────────────────────────
// Logout
// ────────────────────────────────────────────────────────────────────────────
export function clearSession() {
  setMemToken("");
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(PROP_KEY);
  sessionStorage.removeItem("accountingSelectedProperty");
  // Legacy cleanup in case old localStorage keys remain
  ["session:user","session:token","selectedProperty","accountingSelectedProperty"]
    .forEach((k) => localStorage.removeItem(k));
}

export async function logout(skipRedirect = false) {
  try {
    await apiFetch("/api/auth/logout", { method: "POST", credentials: "include" });
  } catch { /* best-effort */ }
  clearSession();
  if (!skipRedirect) window.location.href = "/login";
}

export async function logoutAllDevices() {
  try {
    await apiFetch("/api/auth/logout-all", { method: "POST", auth: true, credentials: "include" });
  } catch { /* best-effort */ }
  clearSession();
  window.location.href = "/login";
}

// ────────────────────────────────────────────────────────────────────────────
// Change password
// ────────────────────────────────────────────────────────────────────────────
export async function changePassword({ currentPassword, newPassword }) {
  return apiFetch("/api/auth/change-password", {
    method: "POST",
    auth:   true,
    body:   { currentPassword, newPassword },
  });
}
